import { ChangeEvent, FC, useEffect, useState } from 'react';
import Loader from '../../components/loader/Loader';
import { useNotify } from '../../components/notifications/NotificationsProvider';
import { useWallet } from '../../components/wallet/WalletProvider';
import { addEmployee } from '../../services/salaryActions';
import { NewEmployee, validateNewEmployee } from '../../utils/validateForm';
import { AddEmployeeModalProps } from '../../interfaces/props/AddEmployeeModalProps';
import './AddEmployeeModal.css';

type Field = keyof NewEmployee;

const AddEmployeeModal: FC<AddEmployeeModalProps> = ({
  employees,
  onClose,
  onChanged,
}) => {
  const { isReady } = useWallet();
  const notify = useNotify();
  const [newEmployee, setNewEmployee] = useState<NewEmployee>({
    full_name: '',
    wallet_address: '',
    salary: '',
  });
  const [fieldFocused, setFieldFocused] = useState<Record<Field, boolean>>({
    full_name: false,
    wallet_address: false,
    salary: false,
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewEmployee({ ...newEmployee, [name]: value });
  };

  const handleFieldFocus = (field: Field) => {
    setFieldFocused({ ...fieldFocused, [field]: true });
  };

  const handleFieldBlur = (field: Field) => {
    if (newEmployee[field] === '') {
      setFieldFocused({ ...fieldFocused, [field]: false });
    }
  };

  const handleAdd = async () => {
    const validationError = validateNewEmployee(newEmployee, employees);
    setError(validationError);
    if (validationError) return;

    setLoading(true);

    try {
      const fullName = newEmployee.full_name.trim();
      await addEmployee(
        fullName,
        newEmployee.wallet_address.trim(),
        newEmployee.salary.trim(),
      );
      notify.success(`Сотрудник ${fullName} добавлен`);
      onChanged();
      onClose();
    } catch (addError) {
      notify.error((addError as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const input = (field: Field, placeholder: string) => (
    <div className="add-employee-modal__field">
      <input
        type="text"
        name={field}
        value={newEmployee[field]}
        onChange={handleInputChange}
        onFocus={() => handleFieldFocus(field)}
        onBlur={() => handleFieldBlur(field)}
        placeholder={placeholder}
        className={`add-employee-modal__input ${fieldFocused[field] ? 'add-employee-modal__input--focused' : ''}`}
      />
    </div>
  );

  return (
    <div className="add-employee-modal__overlay">
      <div className="add-employee-modal__container">
        <h2 className="add-employee-modal__title">Добавить сотрудника</h2>
        {error && <div className="add-employee-modal__error">{error}</div>}
        {!isReady && (
          <div className="add-employee-modal__error">
            Подключите кошелёк в сети BSC
          </div>
        )}
        {input('full_name', 'ФИО сотрудника')}
        {input('wallet_address', 'Адрес кошелька')}
        {input('salary', 'Оклад')}
        <div className="add-employee-modal__actions">
          <button
            className="add-employee-modal__link-button"
            onClick={handleAdd}
            disabled={!isReady || loading}
          >
            Добавить
          </button>
          <button
            className="add-employee-modal__close-button"
            onClick={onClose}
          >
            Отмена
          </button>
        </div>
      </div>
      {loading && <Loader />}
    </div>
  );
};

export default AddEmployeeModal;
